import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store';
import { closeModal } from 'store/layoutSlice';
import {
    archiveProject,
    deleteProject,
    fetchProjectTasks,
    unarchiveProject,
} from 'store/projectsThunks';
import { selectProject } from 'store/workspaceSlice';

import Modal from 'layout/atoms/Modal';
import Project from 'molecules/Project/Project';
import { useEffect } from 'react';

type ProjectModalProps = {
    id?: string;
    addTask?: boolean;
    archived?: boolean;
};

const ProjectModal = ({ id: projectId, addTask = false, archived = false }: ProjectModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const modalName = archived ? 'archivedProjectDetails' : 'projectDetails';

    // Get project data to extract workspaceId for shared projects
    const projectData = useSelector((state: any) =>
        archived
            ? null
            : selectProject(state, projectId || '')
    );
    const sharedWorkspaceId = projectData?.shared?.workspaceId;

    useEffect(() => {
        if (projectId) {
            // For shared projects, use the owner's workspaceId; for own projects, undefined
            dispatch(fetchProjectTasks({ projectId, workspaceId: sharedWorkspaceId, archived }));
        }
    }, [dispatch, projectId, archived, sharedWorkspaceId]);

    const handleClose = () => {
        dispatch(closeModal({ name: modalName }));
    };

    const handleArchiveProject = () => {
        if (projectId) {
            dispatch(archiveProject(projectId))
                .then(handleClose)
                .catch(() => handleClose());
        }
    };

    const handleUnarchiveProject = () => {
        if (projectId) {
            // Close modal immediately to prevent app becoming unresponsive
            handleClose();

            // Then dispatch the unarchive action
            dispatch(unarchiveProject(projectId)).catch((error) => {
                console.error('Unarchive failed:', error);
            });
        }
    };

    const handleDeleteProject = () => {
        if (projectId) {
            dispatch(deleteProject(projectId))
                .then(handleClose)
                .catch(() => handleClose());
        }
    };

    return (
        <Modal open={true} size="l" onClose={handleClose} noStyling>
            {projectId && (
                <Project
                    key={projectId}
                    id={projectId}
                    onProjectDelete={handleDeleteProject}
                    onProjectArchive={handleArchiveProject}
                    onProjectUnarchive={handleUnarchiveProject}
                    archived={archived}
                    modal
                    addTask={addTask}
                />
            )}
        </Modal>
    );
};

export default ProjectModal;
